import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useAnalytics } from "../contexts/analytics/hooks";
import { backendService } from "../services/backend.service";

export const DEPARTMENT_SILENT_JOBS_QUERY_KEY = "department-silent-jobs";

/** Each employee's silent jobs, keyed by nurse id. A missing nurse means all their jobs notify. */
export type SilentJobIdsByNurseId = ReadonlyMap<number, ReadonlySet<string>>;

// DEFAULT: no markers while loading or after an error, since the marker is only a hint.
// One shared instance keeps the columns' memo stable.
const EMPTY: SilentJobIdsByNurseId = new Map();

/** The marker is only a hint, so a minute of staleness is fine. */
const SILENT_JOBS_STALE_TIME_MS = 60_000;

/**
 * Employees in the department with at least one silent job, for the Team list's bell marker.
 * Errors are tracked, not shown, because the roster still works without the markers.
 */
export const useDepartmentSilentJobs = (departmentId: string) => {
	const { trackError } = useAnalytics();
	const query = useQuery({
		queryKey: [DEPARTMENT_SILENT_JOBS_QUERY_KEY, departmentId],
		queryFn: async () => (await backendService.getDepartmentSilentJobs(departmentId)).data,
		staleTime: SILENT_JOBS_STALE_TIME_MS,
	});

	useEffect(() => {
		if (!query.isError) return;
		trackError("team_silent_jobs_fetch_error", { department_id: departmentId, error_message: query.error?.message });
	}, [query.isError, query.error, departmentId, trackError]);

	const silentJobIdsByNurseId = useMemo<SilentJobIdsByNurseId>(() => {
		if (!query.data) return EMPTY;
		return new Map(Object.entries(query.data.silentJobIdsByNurse).map(([nurseId, jobIds]) => [Number(nurseId), new Set(jobIds)]));
	}, [query.data]);

	return { silentJobIdsByNurseId, isLoading: query.isLoading, isError: query.isError };
};
